/**
 * Splits a user's allocation toward a policy into the cash-flow components the
 * shared engine needs to model correctly:
 *
 * - `lumpSumDeduction` — paid today, subtracted from `existingAssetsValue` at
 *   year 0 (matches real-world cash flow for any lump-sum portion of the
 *   premium).
 * - `recurringDrawdowns` — paid over the policy's pay window. Modeled as
 *   `additionalDrawdowns` entries on the cloned funding source so that
 *   `calcSelfFundingProjectedCostCoverage` keeps compounding the un-paid
 *   principal year-by-year while subtracting each year's premium share.
 *
 * For policies with `policyPremiumIncreaseRate`, one drawdown is emitted per
 * year to mirror the scheduled premium step-ups (matching the engine's
 * `calcTotalWithAnnualIncrease` curve). Otherwise a single uniform drawdown
 * spans the pay window.
 */

export interface PremiumCashFlow {
  lumpSumDeduction: number;
  recurringDrawdowns: Drawdown[];
}

interface SplitArgs {
  allocatedAmount: number;
  policy: PendingPolicyFundingSource;
  ltcAtYear: number | null | undefined;
}

/**
 * Build the per-year premium schedule that mirrors
 * `calcTotalWithAnnualIncrease`: increases compound for the first
 * `appliedIncreaseYears` years, then hold flat.
 */
function buildPremiumYearlyAmounts(
  monthlyAmount: number,
  premiumPayYears: number,
  increaseRate: number,
  appliedIncreaseYears: number,
): number[] {
  // Mirrors `calcTotalWithAnnualIncrease`: push the current year's amount, then
  // apply the increase. After `appliedIncreaseYears` pushes the premium has
  // been bumped `appliedIncreaseYears` times and stays at that level for the
  // remaining pay years.
  const amounts: number[] = [];
  let current = monthlyAmount;
  for (let year = 0; year < premiumPayYears; year++) {
    amounts.push(current * 12);
    if (year < appliedIncreaseYears) {
      current *= 1 + increaseRate;
    }
  }
  return amounts;
}

export function splitAllocationByPaymentSchedule({
  allocatedAmount,
  policy,
  ltcAtYear,
}: SplitArgs): PremiumCashFlow {
  if (!allocatedAmount || allocatedAmount <= 0) {
    return { lumpSumDeduction: 0, recurringDrawdowns: [] };
  }

  const lumpSum = Math.max(0, policy.policyLumpSumPayment ?? 0);
  const monthlyPremium = Math.max(0, policy.policyPremiumMonthlyCost ?? 0);
  const premiumStartYear = policy.policyPremiumStartYear ?? new Date().getFullYear();
  const increaseRate = policy.policyPremiumIncreaseRate ?? 0;
  const requestedIncreaseYears = policy.policyPremiumIncreaseYears ?? 0;

  const yearsToLtc =
    ltcAtYear != null && ltcAtYear > premiumStartYear ? ltcAtYear - premiumStartYear : 0;
  const premiumPayYears = policy.policyLimitedPayYears
    ? Math.min(policy.policyLimitedPayYears, yearsToLtc || policy.policyLimitedPayYears)
    : yearsToLtc;

  const hasIncreaseRate = increaseRate > 0 && monthlyPremium > 0 && premiumPayYears > 0;
  const appliedIncreaseYears = hasIncreaseRate
    ? requestedIncreaseYears > 0
      ? Math.min(requestedIncreaseYears, premiumPayYears)
      : premiumPayYears
    : 0;

  const yearlyAmounts = hasIncreaseRate
    ? buildPremiumYearlyAmounts(monthlyPremium, premiumPayYears, increaseRate, appliedIncreaseYears)
    : null;

  const recurringTotalCost = yearlyAmounts
    ? yearlyAmounts.reduce((sum, amount) => sum + amount, 0)
    : monthlyPremium * 12 * premiumPayYears;
  const policyTotalCost = lumpSum + recurringTotalCost;

  // No identifiable recurring portion (single-pay or missing schedule data) —
  // treat the entire allocation as a lump-sum cash outflow today.
  if (recurringTotalCost <= 0 || premiumPayYears <= 0) {
    return { lumpSumDeduction: allocatedAmount, recurringDrawdowns: [] };
  }

  // Split the user's allocation between lump-sum and recurring proportionally
  // to the policy's cost composition.
  let lumpSumDeduction = 0;
  let recurringTotalForSource: number;
  if (lumpSum <= 0) {
    recurringTotalForSource = allocatedAmount;
  } else {
    const lumpSumShare = lumpSum / policyTotalCost;
    lumpSumDeduction = allocatedAmount * lumpSumShare;
    recurringTotalForSource = allocatedAmount - lumpSumDeduction;
  }

  // Uniform premium → one drawdown over the full window. Increasing premium →
  // one drawdown per year to mirror the step-up schedule.
  let recurringDrawdowns: Drawdown[];
  if (yearlyAmounts) {
    const shareRatio = recurringTotalForSource / recurringTotalCost;
    recurringDrawdowns = yearlyAmounts.map((amount, idx) => ({
      startYear: premiumStartYear + idx,
      endYear: premiumStartYear + idx + 1,
      annualAmount: amount * shareRatio,
    }));
  } else {
    recurringDrawdowns = [
      {
        startYear: premiumStartYear,
        endYear: premiumStartYear + premiumPayYears,
        annualAmount: recurringTotalForSource / premiumPayYears,
      },
    ];
  }

  return { lumpSumDeduction, recurringDrawdowns };
}
